// The Azure Map component must always be wrapped in a MapContextWrapper component to work properly.
import React, {
  lazy,
  startTransition,
  Suspense,
  useEffect,
  useState,
} from "react";
import {
  IAzureMapOptions,
  AuthenticationType,
  useAzureMaps,
  AzureMapsProvider, // This is component must be used to wrap the AzureMap component and cannot be lazy loaded.
} from "react-azure-maps";
import "azure-maps-control/dist/atlas.min.css";
import atlas, { ControlPosition } from "azure-maps-control";
import {
  maxLatitude,
  maxLongitude,
  minLatitude,
  minLongitude,
} from "../../constants";
import { getAzureMapsSubscriptionKey } from "../../api/secretsApi";
import { BeatLoader } from "react-spinners";
import tailwindClassnames from "./TailwindClassnames";
import { useTranslation } from "react-i18next";

// Lazy load the AzureMap components.
const AzureMap = lazy(() =>
  import("react-azure-maps").then((module) => ({ default: module.AzureMap })),
);
const AzureMapDataSourceProvider = lazy(() =>
  import("react-azure-maps").then((module) => ({
    default: module.AzureMapDataSourceProvider,
  })),
);
const AzureMapHtmlMarker = lazy(() =>
  import("react-azure-maps").then((module) => ({
    default: module.AzureMapHtmlMarker,
  })),
);

interface MapProps {
  latitude: number | string | null;
  longitude: number | string | null;
  mapZoom?: number;
  mapHeight?: string;
}

const Map: React.FC<MapProps> = ({
  latitude,
  longitude,
  mapZoom = 10,
  mapHeight = "300px",
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [position, setPosition] = useState<atlas.data.Position | undefined>(
    undefined,
  );
  const [subscriptionKey, setSubscriptionKey] = useState<string | null>(null);
  const [mapOptions, setMapOptions] = useState<IAzureMapOptions | null>(null);

  // Validates the latitude and longitude values.
  useEffect(() => {
    if (latitude && longitude) {
      const parsedLatitude = parseFloat(latitude.toString());
      const parsedLongitude = parseFloat(longitude.toString());
      if (parsedLatitude < minLatitude || parsedLatitude > maxLatitude) {
        return;
      }
      if (parsedLongitude < minLongitude || parsedLongitude > maxLongitude) {
        return;
      }
      setPosition([parsedLongitude, parsedLatitude]);
    }
  }, [latitude, longitude]);

  // Get the map reference and check if the map is ready.
  const { mapRef, isMapReady } = useAzureMaps();

  useEffect(() => {
    const fetchSubscriptionKey = async () => {
      const key = await getAzureMapsSubscriptionKey();
      setSubscriptionKey(key);
    };

    fetchSubscriptionKey();
  }, []);

  useEffect(() => {
    if (subscriptionKey) {
      setMapOptions({
        authOptions: {
          authType: AuthenticationType.subscriptionKey,
          subscriptionKey,
        },
        center: position,
        zoom: mapZoom,
      });
    }
  }, [subscriptionKey]);

  const mapControls = [
    {
      controlName: "ZoomControl",
      options: {
        position: ControlPosition.BottomRight,
      },
    },
  ];
  const markerOptions: atlas.HtmlMarkerOptions = {
    position: position,
  };

  // Starts a transition to mark the component as ready to be loaded since we are using lazy loading.
  useEffect(() => {
    startTransition(() => {
      setIsLoaded(true);
    });
  }, []);

  // Re-centers map when the position changes.
  useEffect(() => {
    if (isMapReady && mapRef) {
      mapRef.setCamera({
        center: position,
      });
    }
  }, [position]);

  return (
    <Suspense fallback={<div>{t("Loading...")}</div>}>
      {isLoaded && mapOptions ? (
        <div style={{ height: mapHeight }}>
          <AzureMap options={mapOptions} controls={mapControls}>
            <AzureMapDataSourceProvider
              id="dataSourceProvider"
              options={{ cluster: true, clusterRadius: 2 }}
            >
              <AzureMapHtmlMarker options={markerOptions} />
            </AzureMapDataSourceProvider>
          </AzureMap>
        </div>
      ) : (
        <div className="flex justify-center">
          <BeatLoader
            color={tailwindClassnames.skyBlueDark}
            loading={true}
            size={15}
            margin={5}
          />
        </div>
      )}
    </Suspense>
  );
};

function withWrapper(WrappedComponent: React.FC<MapProps>) {
  return function WrapperComponent(props: MapProps) {
    return (
      <AzureMapsProvider>
        <WrappedComponent {...props} />
      </AzureMapsProvider>
    );
  };
}

export default withWrapper(Map);
